import React from 'react';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';

function PrivacyPolicyPage() {

  return (
    <div className="news-portal">
      <SiteHeader />

      {/* Main Content */}
      <main className="news-main">
        <div className="container">
          <div className="legal-page">
            <h1 className="legal-page-title">Privacy Policy</h1>
            <p className="legal-page-updated">Last updated March 19, 2026</p>

            <p>
              This Privacy Notice for Guppy Games Inc ("we," "us," or "our"), describes how and why we might access, collect, store, use, and/or share ("process") your personal information when you use our services ("Services"), including when you visit our website at <a href="https://www.dailyhealth.news">https://www.dailyhealth.news</a> or engage with us in other related ways.
            </p>
            <p>
              Questions or concerns? Reading this Privacy Notice will help you understand your privacy rights and choices. If you do not agree with our policies and practices, please do not use our Services. If you still have any questions or concerns, please contact us at <a href="mailto:info@dailyhealth.news">info@dailyhealth.news</a>.
            </p>

            <h2>Summary of Key Points</h2>
            <ul>
              <li><strong>What personal information do we process?</strong> When you visit, use, or navigate our Services, we may process personal information depending on how you interact with us and the Services, the choices you make, and the products and features you use.</li>
              <li><strong>Do we process any sensitive personal information?</strong> We do not process sensitive personal information.</li>
              <li><strong>Do we collect any information from third parties?</strong> We do not collect any information from third parties.</li>
              <li><strong>How do we process your information?</strong> We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law.</li>
              <li><strong>How do we keep your information safe?</strong> We have adequate organizational and technical processes and procedures in place to protect your personal information. However, no electronic transmission over the internet or information storage technology can be guaranteed to be 100% secure.</li>
            </ul>

            <h2>Table of Contents</h2>
            <ol className="legal-toc">
              <li>What Information Do We Collect?</li>
              <li>How Do We Process Your Information?</li>
              <li>What Legal Bases Do We Rely On to Process Your Personal Information?</li>
              <li>When and With Whom Do We Share Your Personal Information?</li>
              <li>What Is Our Stance on Third-Party Websites?</li>
              <li>Do We Use Cookies and Other Tracking Technologies?</li>
              <li>How Do We Handle Your Social Logins?</li>
              <li>How Long Do We Keep Your Information?</li>
              <li>How Do We Keep Your Information Safe?</li>
              <li>Do We Collect Information from Minors?</li>
              <li>What Are Your Privacy Rights?</li>
              <li>Controls for Do-Not-Track Features</li>
              <li>Do United States Residents Have Specific Privacy Rights?</li>
              <li>Do Other Regions Have Specific Privacy Rights?</li>
              <li>Do We Make Updates to This Notice?</li>
              <li>How Can You Contact Us About This Notice?</li>
              <li>How Can You Review, Update, or Delete the Data We Collect From You?</li>
            </ol>

            <h2>1. What Information Do We Collect?</h2>
            <h3>Personal information you disclose to us</h3>
            <p>
              <em>In Short: We collect personal information that you provide to us.</em>
            </p>
            <p>
              We collect personal information that you voluntarily provide to us when you register on the Services, express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us. The personal information we collect may include names, phone numbers, email addresses, and mailing addresses.
            </p>
            <p>
              <strong>Sensitive Information.</strong> We do not process sensitive information.
            </p>
            <p>
              <strong>Social Media Login Data.</strong> We may provide you with the option to register with us using your existing social media account details, like your Facebook, X, or other social media account.
            </p>

            <h3>Information automatically collected</h3>
            <p>
              <em>In Short: Some information — such as your Internet Protocol (IP) address and/or browser and device characteristics — is collected automatically when you visit our Services.</em>
            </p>
            <p>
              We automatically collect certain information when you visit, use, or navigate the Services. This information does not reveal your specific identity but may include device and usage information, such as your IP address, browser and device characteristics, operating system, language preferences, referring URLs, device name, country, location, information about how and when you use our Services, and other technical information.
            </p>
            <p>
              The information we collect includes:
            </p>
            <ul>
              <li><strong>Log and Usage Data.</strong> Service-related, diagnostic, usage, and performance information our servers automatically collect when you access or use our Services.</li>
              <li><strong>Device Data.</strong> Information about your computer, phone, tablet, or other device you use to access the Services, including your IP address, device and application identification numbers, location, browser type, hardware model, Internet service provider and/or mobile carrier, operating system, and system configuration information.</li>
              <li><strong>Location Data.</strong> We collect location data such as information about your device's location, which can be either precise or imprecise.</li>
            </ul>
            <p>
              <strong>Google API.</strong> Our use of information received from Google APIs will adhere to Google API Services User Data Policy, including the Limited Use requirements.
            </p>

            <h2>2. How Do We Process Your Information?</h2>
            <p>
              <em>In Short: We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law.</em>
            </p>
            <p>We process your personal information for a variety of reasons, including:</p>
            <ul>
              <li>To facilitate account creation and authentication and otherwise manage user accounts.</li>
              <li>To request feedback and to contact you about your use of our Services.</li>
              <li>To send you marketing and promotional communications (you can opt out at any time).</li>
              <li>To deliver targeted advertising to you.</li>
              <li>To protect our Services from fraud and security threats.</li>
              <li>To identify usage trends and improve our Services.</li>
              <li>To determine the effectiveness of our marketing and promotional campaigns.</li>
            </ul>

            <h2>3. What Legal Bases Do We Rely On to Process Your Information?</h2>
            <p>
              <em>In Short: We only process your personal information when we believe it is necessary and we have a valid legal reason to do so under applicable law.</em>
            </p>
            <p>
              If you are located in the EU or UK, we rely on consent, legitimate interests, legal obligations, or vital interests as applicable. If you are located in Canada, we may process your information based on express or implied consent, or in exceptional cases without consent as permitted by applicable law.
            </p>

            <h2>4. When and With Whom Do We Share Your Personal Information?</h2>
            <p>
              <em>In Short: We may share information in specific situations described in this section and/or with the following third parties.</em>
            </p>
            <p>
              <strong>Business Transfers.</strong> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.
            </p>

            <h2>5. What Is Our Stance on Third-Party Websites?</h2>
            <p>
              <em>In Short: We are not responsible for the safety of any information that you share with third parties that we may link to or who advertise on our Services, but are not affiliated with, our Services.</em>
            </p>
            <p>
              The Services may link to third-party websites, online services, or mobile applications and/or contain advertisements from third parties that are not affiliated with us. We cannot guarantee the safety and privacy of data you provide to any third-party websites. Any data collected by third parties is not covered by this Privacy Notice.
            </p>

            <h2>6. Do We Use Cookies and Other Tracking Technologies?</h2>
            <p>
              <em>In Short: We may use cookies and other tracking technologies to collect and store your information.</em>
            </p>
            <p>
              We may use cookies and similar tracking technologies (like web beacons and pixels) to gather information when you interact with our Services. We also permit third parties and service providers to use online tracking technologies on our Services for analytics and advertising.
            </p>
            <p>
              <strong>Google Analytics.</strong> We may share your information with Google Analytics to track and analyze the use of the Services. To opt out of being tracked by Google Analytics, visit <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">https://tools.google.com/dlpage/gaoptout</a>.
            </p>

            <h2>7. How Do We Handle Your Social Logins?</h2>
            <p>
              <em>In Short: If you choose to register or log in to our Services using a social media account, we may have access to certain information about you.</em>
            </p>
            <p>
              Our Services offer you the ability to register and log in using your third-party social media account details (like your Facebook or X logins). Where you choose to do this, we will receive certain profile information about you from your social media provider, including your name, email address, friends list, and profile picture.
            </p>

            <h2>8. How Long Do We Keep Your Information?</h2>
            <p>
              <em>In Short: We keep your information for as long as necessary to fulfill the purposes outlined in this Privacy Notice unless otherwise required by law.</em>
            </p>
            <p>
              We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy Notice, unless a longer retention period is required or permitted by law. When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize such information.
            </p>

            <h2>9. How Do We Keep Your Information Safe?</h2>
            <p>
              <em>In Short: We aim to protect your personal information through a system of organizational and technical security measures.</em>
            </p>
            <p>
              We have implemented appropriate and reasonable technical and organizational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure.
            </p>

            <h2>10. Do We Collect Information from Minors?</h2>
            <p>
              <em>In Short: We do not knowingly collect data from or market to children under 18 years of age.</em>
            </p>
            <p>
              We do not knowingly collect, solicit data from, or market to children under 18 years of age, nor do we knowingly sell such personal information. By using the Services, you represent that you are at least 18 years old. If you become aware of any data we may have collected from children under age 18, please contact us at <a href="mailto:info@dailyhealth.news">info@dailyhealth.news</a>.
            </p>

            <h2>11. What Are Your Privacy Rights?</h2>
            <p>
              <em>In Short: Depending on your state of residence in the US or your region, you have rights that allow you greater access to and control over your personal information.</em>
            </p>
            <p>
              In some regions (like the EEA, UK, Switzerland, and Canada), you have certain rights under applicable data protection laws, including the right to request access and obtain a copy of your personal information, to request rectification or erasure, to restrict the processing of your personal information, and to data portability.
            </p>
            <p>
              <strong>Opting out of marketing communications:</strong> You can unsubscribe from our marketing and promotional communications at any time by clicking on the unsubscribe link in the emails that we send, or by contacting us.
            </p>
            <p>
              If you have questions or comments about your privacy rights, you may email us at <a href="mailto:info@dailyhealth.news">info@dailyhealth.news</a>.
            </p>

            <h2>12. Controls for Do-Not-Track Features</h2>
            <p>
              Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track ("DNT") feature or setting you can activate to signal your privacy preference not to have data about your online browsing activities monitored and collected. At this stage, no uniform technology standard for recognizing and implementing DNT signals has been finalized. As such, we do not currently respond to DNT browser signals.
            </p>

            <h2>13. Do United States Residents Have Specific Privacy Rights?</h2>
            <p>
              <em>In Short: If you are a resident of California, Colorado, Connecticut, Delaware, Florida, Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon, Rhode Island, Tennessee, Texas, Utah, or Virginia, you may have specific rights regarding your personal information.</em>
            </p>
            <p>
              These rights may include: the right to know whether or not we are processing your personal data; the right to access, correct, or delete your personal data; the right to obtain a copy of the personal data you previously shared with us; and the right to non-discrimination for exercising your rights.
            </p>
            <p>
              To exercise these rights, you can contact us by emailing us at <a href="mailto:info@dailyhealth.news">info@dailyhealth.news</a> or by referring to the contact details at the bottom of this document.
            </p>

            <h2>14. Do Other Regions Have Specific Privacy Rights?</h2>
            <h3>Australia</h3>
            <p>
              We collect and process your personal information under the obligations and conditions set by Australia's Privacy Act 1988. This Privacy Notice satisfies the notice requirements defined in the Privacy Act. If you believe we are unlawfully processing your personal information, you have the right to submit a complaint about a breach of the Australian Privacy Principles to the Office of the Australian Information Commissioner.
            </p>

            <h2>15. Do We Make Updates to This Notice?</h2>
            <p>
              <em>In Short: Yes, we will update this notice as necessary to stay compliant with relevant laws.</em>
            </p>
            <p>
              We may update this Privacy Notice from time to time. The updated version will be indicated by an updated "Revised" date at the top of this Privacy Notice. We encourage you to review this Privacy Notice frequently to be informed of how we are protecting your information.
            </p>

            <h2>16. How Can You Contact Us About This Notice?</h2>
            <p>
              If you have questions or comments about this notice, you may email us at <a href="mailto:info@dailyhealth.news">info@dailyhealth.news</a> or contact us by post at:
            </p>
            <address>
              Guppy Games Inc<br />
              1730 Minor Ave, Suite 1050<br />
              Seattle, WA 98101<br />
              United States
            </address>

            <h2>17. How Can You Review, Update, or Delete the Data We Collect From You?</h2>
            <p>
              Based on the applicable laws of your country or state of residence in the US, you may have the right to request access to the personal information we collect from you, details about how we have processed it, correct inaccuracies, or delete your personal information. You may also have the right to withdraw your consent to our processing of your personal information. To request to review, update, or delete your personal information, please contact us at <a href="mailto:info@dailyhealth.news">info@dailyhealth.news</a>.
            </p>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

export default PrivacyPolicyPage;
